import type { PublicTag, TagCategory, UpdatePeriod } from '@/types/api'

/**
 * 站点标签模型：**每个作品固定 5 栏**。
 *
 * 这个文件曾经在 `src/api/mocks/taxonomy.ts`，靠一份前端名单 + 命名规则反查
 * 「这个标签属于哪一栏」。**后端已于 2026-08-08 补上真实字段**，那套猜测全部
 * 删掉了（差异记录见 docs/backend-sync/2026-08-07-tag-facets-diff.md 的
 * 「已解决」一节）。现在五栏的来源是：
 *
 * | 栏目 | id | 数据来源 | 点击后 |
 * |---|---|---|---|
 * | 类型 | `type` | `PublicTag.category === 'type'` | `/t/<public_id>` |
 * | 社团 | `author` | `authors` 关系，**不是标签** | `?author_name=<名称>` |
 * | 平台 | `game` | `PublicTag.category === 'game'` | `/t/<public_id>` |
 * | 其他 | `other` | `PublicTag.category === 'other'` | `/t/<public_id>` |
 * | 更新时间 | `updated` | `Post.update_period`（`YYYY-MM`） | `?update_year=<年份>` |
 *
 * ## 为什么三栏标签落到 `/t/{public_id}` 而不是 `/search?tag_id=`
 *
 * 站内点标签的入口有四个：标签大全、侧栏导航、首页磁贴、详情页右栏。它们指向的
 * 必须是**同一个**筛选结果页，否则「按下载量重排」这类操作会在一处有、另一处没有。
 * `/t/:publicId` 就是那个页面（走后端专用的 `GET /tags/{public_id}/posts`）。
 *
 * 社团与更新时间仍然落到搜索页：它们不是标签，也没有各自的落地页。
 *
 * ## 为什么用 `public_id` 而不是标签名
 *
 * 后端的标签名只在 `(category, name)` 上唯一 —— 同一个名字**可以**既是平台又是
 * 类型。按名检索会跨栏一起命中，用户点的是「平台 › Steam」，拿到的却混着
 * 「类型 › Steam」。`public_id` 精确到那一行。
 *
 * `?tag=<名称>`（按名）仍然受支持，用于手写或历史链接 —— 它是后端四种条件类型
 * 之一，不是前端的兜底。见 features/search/useSearchFilters.ts。
 *
 * ## 为什么「社团」不是标签
 *
 * 后端把它建模成一等实体（`creators` 表、`post_revision_authors` 关联、
 * `author_name` / `author_id` 两种条件）。降级成标签会丢掉 `credit_role`、
 * `sort_order` 与社团主页。后端也因此**刻意没有** `author` 这个 category。
 *
 * ## 为什么「更新时间」点击后搜的是整年
 *
 * 更新时间是发布者手填的 `YYYY-MM`，按月搜出来往往只有个位数结果，列表页没有浏览
 * 价值。所以**展示到月、检索到年**，后端为此提供的正是 `update_year` 这个
 * 按整年过滤的参数（它是过滤器而不是条件：无条件 AND，不参与 `match=any`）。
 */

/** 五栏的 id。前三个与后端 `TagCategory` 同名且同义，`author` / `updated` 不是标签。 */
export type TagFacetId = TagCategory | 'author' | 'updated'

export interface TagFacet {
  id: TagFacetId
  label: string
  /**
   * 详情页在该栏没有值时显示的占位说明。
   *
   * **省略即表示这一栏可选**：没有值时详情页整行不渲染，而不是留一句占位（见
   * `TagGroups`）。这个字段因此同时编码了两件事——占位文案，以及"缺了要不要
   * 说出来"。拆成两个字段就会出现"可选但配了占位文案"这种自相矛盾的组合。
   */
  emptyHint?: string
}

/**
 * 详情页右栏与筛选面板的展示顺序。
 *
 * 前四栏**固定渲染**：缺了也要留一句占位，理由见 `TagGroups`。
 * 「更新时间」是唯一的例外（没有 `emptyHint`）——它不是标签，而是发布者手填的
 * 一个可选事实，没填就是没这回事，留一行「未标注」只是噪音。
 */
export const TAG_FACETS: readonly TagFacet[] = [
  { id: 'type', label: '类型', emptyHint: '未标注类型' },
  { id: 'author', label: '社团', emptyHint: '未标注社团' },
  { id: 'game', label: '平台', emptyHint: '未标注平台' },
  { id: 'other', label: '其他', emptyHint: '没有其他标签' },
  { id: 'updated', label: '更新时间' },
]

/** 侧栏导航与筛选面板里按栏目取值的那三栏（社团与更新时间各有自己的来源）。 */
export const TAG_CATEGORIES: readonly TagCategory[] = ['type', 'game', 'other']

/** 栏目 id → 中文名，用于 chip 文案「类型 › 整合包」。 */
export function facetLabel(id: TagFacetId): string {
  return TAG_FACETS.find((facet) => facet.id === id)?.label ?? id
}

/* -------------------------------------------------------------- 更新时间 */

/** 后端 `UpdatePeriod` 的形状：`YYYY-MM`，01–12。 */
const UPDATE_PERIOD_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/

export interface UpdateMonth {
  year: number
  /** 1–12。 */
  month: number
}

/** `'2026-08'` → `{year: 2026, month: 8}`。`null` / 形状不符时返回 undefined。 */
export function parseUpdatePeriod(value: UpdatePeriod | undefined): UpdateMonth | undefined {
  if (!value) return undefined
  const match = UPDATE_PERIOD_PATTERN.exec(value)
  if (!match?.[1] || !match[2]) return undefined
  return { year: Number(match[1]), month: Number(match[2]) }
}

/** `{2026, 8}` → `2026 年 8 月`。 */
export function formatUpdateMonth(value: UpdateMonth): string {
  return `${value.year} 年 ${value.month} 月`
}

export function formatUpdateYear(year: number | string): string {
  return `${year} 年`
}

/** `'2026-08'` → `'2026 年 8 月'`；未标注时返回 undefined，由调用方决定占位文案。 */
export function formatUpdatePeriod(value: UpdatePeriod | undefined): string | undefined {
  const parsed = parseUpdatePeriod(value)
  return parsed ? formatUpdateMonth(parsed) : undefined
}

/** `'2026-08'` → `'2026'`。用于「展示到月、检索到年」里的那个年。 */
export function updateYearOf(value: UpdatePeriod | undefined): string | undefined {
  const parsed = parseUpdatePeriod(value)
  return parsed ? String(parsed.year) : undefined
}

/* ------------------------------------------------------------------ 链接 */

/**
 * 所有「点标签去检索」的链接都在这里生成。
 *
 * 集中一处的理由：搜索页的 URL 形状就是后端的查询参数形状（`tag_id` /
 * `author_name` 可重复，`update_year` 单值），散落着手拼字符串迟早会拼出一个
 * 后端 422 的参数名。
 */
/** 手写/历史链接用的按名检索。会跨栏命中同名标签，见文件头。 */
export function searchPathForTagName(name: string): string {
  return `/search?tag=${encodeURIComponent(name)}`
}

export function searchPathForAuthor(displayName: string): string {
  return `/search?author_name=${encodeURIComponent(displayName)}`
}

/** 更新时间点击后落到**整年**。 */
export function searchPathForYear(year: number | string): string {
  return `/search?update_year=${encodeURIComponent(String(year))}`
}

/**
 * 标签的落地页，走后端专用的 `GET /tags/{public_id}/posts`。
 *
 * **站内所有可点标签都用它**：标签大全、侧栏导航、首页磁贴、详情页右栏。
 * 见文件头「为什么三栏标签落到 /t/{public_id}」。
 */
export function tagPath(tag: Pick<PublicTag, 'public_id'>): string {
  return `/t/${encodeURIComponent(tag.public_id)}`
}

/* ------------------------------------------------------------------ 分组 */

export interface TagGroup {
  category: TagCategory
  label: string
  tags: PublicTag[]
}

/**
 * 一批标签 → 按栏目分组，顺序固定为 类型 / 平台 / 其他。
 *
 * 空栏目会被剔除：侧栏里一个没有条目的分组标题只是噪音。栏目内的顺序沿用后端
 * 返回的顺序（`ORDER BY category, name_normalized`），不在前端重排。
 */
export function groupTagsByCategory(tags: readonly PublicTag[]): TagGroup[] {
  return TAG_CATEGORIES.map((category) => ({
    category,
    label: facetLabel(category),
    tags: tags.filter((tag) => tag.category === category),
  })).filter((group) => group.tags.length > 0)
}
